/* eslint-disable no-console */
import { GlobalState } from '@leverege/ui-redux';
import { Toast } from '@leverege/ui-elements';
import { Config } from '@leverege/plugin';
import { Constants } from '@leverege/api-redux';
import { Formatters } from '@leverege/ui-attributes';
import Molten from '@leverege/molten';
import P from '@leverege/promise';
import I18N from '@leverege/i18n';
import { UNPAIRED } from '../constants';
import { getCompanyAndLocationIdsFromUrl } from './Url';
const priceFormat = {
  type: 'attr.formatter.number',
  maxDecimals: 2
};
const {
  t
} = I18N.ns('recovr.salesUtil');

/**
 * 
 * @param {object} options
 * @param {object} options.actions
 * @param {function} options.onChange
 * @param {function} options.onClose
 * @param {object} options.newLocator
 * @param {object} options.vehicle
 * @param {boolean} options.isNewCustomer
 * @param {object} options.newAcct
 * @param {object} options.exAcct
 * @param {boolean} options.vehicleLeftLot
 * @param {string} options.saleType
 * @param {boolean} options.hasCustomer
 * @param {boolean} options.goToSales
 * @param {boolean} options.throwErr
 */
async function sell(options) {
  const {
    actions,
    createdBy,
    customerName,
    exAcct,
    goToSales = true,
    isNewCustomer,
    keys,
    keysSalesPrice,
    locatorSalesPrice,
    newAcct,
    newLocator,
    onChange,
    onClose,
    productTypes,
    salesperson,
    saleType,
    hasCustomer,
    throwErr = false,
    toast = true,
    unpair = false,
    vehicle,
    vehicleLeftLot,
    warrantyLength,
    knowTags,
    qtyOfTags,
    keysWarranty
  } = options;
  const {
    locationId,
    companyId
  } = actions?.match?.params || {};
  const locatorId = newLocator?.id || vehicle?.data?.data?.locator?.id || UNPAIRED;
  onChange?.({
    data: 'loading',
    value: true
  });
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let res;
    try {
      let nApi;
      if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('vehicles').obj(vehicle.id).srv('recovr');
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('vehicles').obj(vehicle.id).srv('recovr');
      }
      res = await nApi.post('sell', {
        saleType,
        customer: resolveCustomer(hasCustomer, isNewCustomer, newAcct, exAcct),
        vehicleId: vehicle.id,
        locatorId,
        locationId,
        vehicleLeftLot,
        unpair,
        customerName,
        salesperson,
        createdBy,
        locatorSalesPrice: Number(Formatters.format(locatorSalesPrice, priceFormat).join('')),
        keysSalesPrice: Number(Formatters.format(keysSalesPrice, priceFormat).join('')),
        productTypes,
        keys,
        warrantyLength,
        knowTags,
        qtyOfTags,
        keysWarranty
      }, {
        force: true
      });
      if (toast) {
        const message = res.message.includes('pending') ? 'a pending sale' : 'sold';
        Toast.success(`Successfully marked vehicle as ${message}.`);
      }
    } catch (err) {
      console.error('Error marking vehicle as sold.', err.message);
      if (toast) {
        Toast.error(['Error marking vehicle as sold.', err?.message]);
      }
      if (throwErr) {
        throw err;
      }
    }
    onClose?.();
    if (goToSales) {
      setTimeout(() => GlobalState.dispatch(actions.parent().parent().child('sales').goTo()), 500);
    }
    return res;
  });
}
export async function canSellVehicleWithUnpairedLocator(options) {
  const {
    actions
  } = options;
  const {
    locationId,
    companyId
  } = actions?.match?.params || {};
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).srv('recovr').get('sellVehicleWithUnpairedLocator', {
          test: true
        });
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).srv('recovr').get('sellVehicleWithUnpairedLocator', {
          test: true
        });
      }
      const res = await nApi;
      return res.allowed;
    } catch (err) {
      console.error({
        err
      }, 'Error retrieving permissions to see a vehicle with an unpaired locator');
      return false;
    }
  });
}
function resolveCustomer(hasCustomer, isNewCustomer, newAcct, exAcct) {
  if (!hasCustomer) {
    return undefined;
  }
  if (isNewCustomer) {
    const {
      location: streetAddress,
      ...addressFields
    } = newAcct?.address || {};
    return {
      name: newAcct.name,
      email: newAcct.email,
      phoneNumber: newAcct.phone,
      streetAddress,
      addressFields
    };
  }
  const {
    location: streetAddress,
    ...addressFields
  } = exAcct?.address || {};
  return {
    email: exAcct.username,
    addressFields,
    name: exAcct.name,
    phoneNumber: exAcct.phone,
    streetAddress
  };
}
async function canForceVoidSale(options) {
  const {
    locationId,
    saleId,
    actions
  } = options;
  const {
    companyId
  } = actions?.match?.params || {};
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      }
      const res = await nApi.post('forceVoid', null, {
        test: true
      });
      return res.allowed;
    } catch (err) {
      console.error(err);
    }
    return false;
  });
}
async function voidSale(options) {
  const {
    locationId,
    saleId,
    actions,
    force,
    goTo = true
  } = options;
  const {
    companyId
  } = actions?.match?.params || {};
  const username = GlobalState.get().auth.profile.name || GlobalState.get().auth.profile.username;
  await GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      }
      const res = await nApi.post(force ? 'forceVoid' : 'void', {
        locationId,
        username
      });
      if (res.warn) {
        Toast.warn([t(`warn.${res.warn}.title`), t(`warn.${res.warn}.message`)]);
      } else {
        Toast.success('Successfully voided sale');
      }
    } catch (err) {
      console.error(err);
      Toast.error(['Error voiding sale', err?.message]);
    }
    if (goTo) {
      // should go nowhere, might trigger reload?
      setTimeout(() => GlobalState.dispatch(actions.goTo()), 500);
    }
  });
}
async function canReplaceLocator(options) {
  const {
    locationId,
    saleId,
    actions
  } = options;
  const {
    companyId
  } = actions?.match?.params || {};
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      }
      const res = await nApi.post('replaceCustomerLocator', null, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: saleId,
        action: 'replaceCustomerLocator',
        allowed: res.allowed === true
      });
      return res.allowed;
    } catch (err) {
      console.error(err);
    }
    return false;
  });
}
async function replaceLocator(options) {
  const {
    newLocatorId,
    sale,
    actions,
    goTo = true,
    onClose
  } = options;
  const salePath = Config.get('VoidSale', 'saleLocationParentPath', 'location');
  const {
    companyId
  } = actions?.match?.params || {};
  const locationId = sale.data?.data?.[salePath]?.id;
  const saleId = sale.id;
  await GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      }
      const res = await nApi.post('replaceCustomerLocator', {
        locatorId: newLocatorId
      }).then(res => {
        Toast.success('Successfully replaced customer locator');
        if (goTo) {
          // should go nowhere, might trigger reload?
          setTimeout(() => GlobalState.dispatch(actions.parent().goTo()), 500);
        }
        return res;
      });
      onClose?.();
      return res;
    } catch (err) {
      console.error(err);
      Toast.error(['Error replacing customer locator', err?.message]);
      throw err;
    }
  });
}
async function canChangeVehicleOwnership(options) {
  const {
    locationId,
    saleId,
    actions
  } = options;
  const {
    companyId
  } = actions?.match?.params || {};
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      }
      const res = await nApi.post('changeVehicleOwnership', null, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: saleId,
        action: 'changeVehicleOwnership',
        allowed: res.allowed === true
      });
      return res.allowed;
    } catch (err) {
      console.error(err);
    }
    return false;
  });
}
export async function canAddAdditionalTagsToSale(options) {
  const {
    sale,
    locationId
  } = options;
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      const nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(sale.id).srv('recovr');
      const res = await nApi.post('addAdditionalTagsToSale', {
        sale,
        additionalTagData: {}
      }, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: sale.id,
        action: 'addAdditionalTagsToSale',
        allowed: res.allowed === true
      });
      return res.allowed;
    } catch (err) {
      console.error({
        err
      }, 'Error retrieving permissions to see if we can add additional tags to sale');
      return false;
    }
  });
}
export async function addAdditionalTagsToSale(options) {
  const {
    sale,
    locationId,
    additionalTagData
  } = options;
  let res;
  await GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      const nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(sale.id).srv('recovr');
      res = await nApi.post('addAdditionalTagsToSale', {
        sale,
        additionalTagData
      });
    } catch (err) {
      Toast.error(`Error additionalTagData: ${err?.message || err}`);
    }
    if (res?.error) {
      Toast.error(res.error ? `Error additionalTagData: ${res.error?.message || res.error}` : 'Error additionalTagData');
    } else {
      Toast.success('Tag added Successfully');
    }
  });
}
async function changeVehicleOwnership(options) {
  const {
    locationId,
    saleId,
    actions,
    goTo = true,
    newCustomer,
    customer
  } = options;
  const {
    companyId
  } = actions?.match?.params || {};
  let res;
  await GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      }
      res = await nApi.post('changeVehicleOwnership', {
        locationId,
        newCustomer,
        customer
      });
      // Custom response to surface errors in UI, need to run multiple checks to catch different origin of the error 
      if (res && !res?.error && (!res.status || res.status === 200 || res.status === 201)) {
        const message = res?.created ? 'Invitation email has been sent' : 'Vehicle ownership changed to existing user';
        Toast.success(message);
      }
    } catch (err) {
      console.error(err);
      Toast.error(`Error changing vehicle ownership: ${err?.message || err}`);
    }
    if (res?.error || res.status >= 400) {
      Toast.error(res.error ? `Error changing vehicle ownership: ${res.error?.message || res.error}` : 'Error changing vehicle ownership');
    }
    if (goTo) {
      // should go nowhere, might trigger reload?
      setTimeout(() => GlobalState.dispatch(actions.parent().goTo()), 500);
    }
  });
}

/**
 * Attempts to find the sale that caused a customer to be created and get the customer info from that sale
 */
async function getCustomerInfo(options) {
  // get location and company id either from params or from url
  const {
    email,
    locationId: paramLocationId,
    companyId: paramCompanyId
  } = options;
  let locationId = paramLocationId;
  let companyId = paramCompanyId;
  if (!locationId || !companyId) {
    const currentPath = GlobalState.get().router?.location.pathname;
    const {
      locationId: urlLocationId,
      companyId: urlCompanyId
    } = getCompanyAndLocationIdsFromUrl(currentPath);
    locationId = urlLocationId;
    companyId = urlCompanyId;
  }
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      const systemId = Molten.getConfig('api/systemId');
      let nApi;
      if (companyId && locationId) {
        nApi = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId);
      } else {
        nApi = api.interface(systemId, 'location').obj(locationId);
      }
      const res = await nApi.srv('recovr').post('getSalesWithCustomerEmail', {
        email
      });
      return findFirstCustomerInfoFromSales(res.items);
    } catch (err) {
      Toast.error(`Error getting customer info: ${err?.message || err}`);
      return null;
    }
  });
}
async function canSendPendingSaleMessage(options) {
  const {
    actions,
    saleId
  } = options;
  const {
    companyId,
    locationId
  } = actions?.match?.params || {};
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi;
    try {
      if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId);
      } else if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('sales').obj(saleId);
      } else if (locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId);
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'sale').obj(saleId);
      }
      const res = await nApi.srv('recovr').post('sendPendingSaleMessage', null, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: saleId,
        action: 'sendPendingSaleMessage',
        allowed: res.allowed === true
      });
      return res.allowed;
    } catch (err) {
      console.error(err);
    }
    return false;
  });
}
async function sendPendingSaleMessage(options) {
  const {
    actions,
    saleId,
    recipients,
    message
  } = options;
  const {
    companyId,
    locationId
  } = actions?.match?.params || {};
  await GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi, res;
    try {
      if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId);
      } else if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('sales').obj(saleId);
      } else if (locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId);
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'sale').obj(saleId);
      }
      res = await nApi.srv('recovr').post('sendPendingSaleMessage', {
        recipients,
        message
      });
      // Custom response to surface errors in UI, need to run multiple checks to catch different origin of the error 
      if (res && !res?.error && (!res.status || res.status === 200 || res.status === 201)) {
        Toast.success('Message Sent');
      }
    } catch (err) {
      console.error(err);
      Toast.error(`Error sending message: ${err?.message || err}`);
    }
    if (res?.error || res?.status >= 400) {
      Toast.error(res.error ? `Error sending message: ${res.error?.message || res.error}` : 'Error sending message');
    }
  });
}
function findFirstCustomerInfoFromSales(sales) {
  const saleWithCustomerInfo = sales.find(sale => {
    const streetAddress = sale?.data?.customer?.streetAddress;
    return streetAddress && streetAddress.trim() !== '';
  });
  return saleWithCustomerInfo?.data?.customer ?? null;
}
async function reconcileSales(options) {
  const {
    companyId,
    locationId,
    sales,
    reconcileState
  } = options;
  try {
    const reconcileSuccesses = [];
    const reconcileErrors = [];
    await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      // wait for all sales to finish and process in parallel
      await P.mapLimit(sales, async sale => {
        const saleId = sale.id;
        let nApi;
        try {
          if (companyId && locationId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId);
          } else if (companyId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('sales').obj(saleId);
          } else {
            nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId);
          }
          const res = await nApi.srv('recovr').post('reconcileSales', {
            saleId,
            reconcileState
          });
          if (res && !res?.error && (!res.status || res.status === 200 || res.status === 201)) {
            reconcileSuccesses.push(res);
          } else {
            reconcileErrors.push(res);
            Toast.error(`Could not save changes to sale: ${saleId}`);
          }
        } catch (err) {
          console.error(`Error getting sale ${saleId}: ${err}`);
        }
      }, {
        limit: 5
      });
      // Consolidate all successes into a single toast
      const reconcileSuccessesLength = reconcileSuccesses.length;
      const reconcileErrorsLength = reconcileErrors.length;
      const reconcileTotal = reconcileSuccesses.length + reconcileErrors.length;
      const reconcileTotalPlural = reconcileTotal > 1 ? 'sales' : 'sale';
      if (reconcileSuccessesLength === 1 && reconcileErrorsLength === 0) {
        Toast.success('Successfully saved changes to sale.');
      } else if (reconcileSuccessesLength > 0) {
        Toast.success(`Successfully saved changes to ${reconcileSuccessesLength} out 
          of ${reconcileTotal} ${reconcileTotalPlural}.`);
      }
    });
  } catch (err) {
    console.error(`Could not save changes: ${err}`);
  }
}
// checks permissions of user by sending a test api call for reconcileSales
async function canReconcileSales(options) {
  const {
    companyId,
    locationId,
    saleId,
    reconcileState
  } = options;
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi;
    try {
      if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId);
      } else if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('sales').obj(saleId);
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId);
      }
      const res = await nApi.srv('recovr').post('reconcileSales', {
        saleId,
        reconcileState
      }, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: saleId,
        action: 'reconcileSales',
        allowed: res.allowed === true
      });
      return res.allowed;
    } catch (err) {
      console.error(`Error on canReconcileSales: ${err}`);
    }
    return false;
  });
}
async function markAsLost(options) {
  const {
    actions,
    locator
  } = options;
  const {
    locationId,
    companyId
  } = actions?.match?.params || {};
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    const locatorId = locator.id;
    try {
      let nApi;
      if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('locators').obj(locatorId);
      } else if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locators').obj(locatorId);
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('locators').obj(locatorId);
      }
      return await nApi.srv('recovr').post('markAsLost', null);
    } catch (err) {
      console.error(`Could not Mark as Lost: ${err.message}`);
      return new Error(err.message);
    }
  });
}

// test api call to check permissions 
async function canMarkAsLost(options) {
  const {
    companyId,
    locationId,
    locatorId
  } = options;
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi;
    if (companyId && locationId) {
      nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('locators').obj(`${locatorId}`);
    } else if (companyId) {
      nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locators').obj(locatorId);
    } else {
      nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('locators').obj(locatorId);
    }
    const res = await nApi.srv('recovr').post('markAsLost', null, {
      test: true
    });
    dispatch({
      type: Constants.ALLOWED_SET,
      resourceId: locatorId,
      action: 'markAsLost',
      allowed: res.allowed === true
    });
    return res.allowed;
  });
}
async function canCreateTripacContract(options) {
  const {
    companyId,
    locationId,
    saleId
  } = options;
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    let nApi;
    if (companyId && locationId) {
      nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId);
    } else if (companyId) {
      nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('sales').obj(saleId);
    } else {
      nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId);
    }
    const res = await nApi.srv('recovr').post('createTripacContract', null, {
      test: true
    });
    dispatch({
      type: Constants.ALLOWED_SET,
      resourceId: saleId,
      action: 'createTripacContract',
      allowed: res.allowed === true
    });
    return res.allowed;
  });
}
async function createTripacContract(options) {
  const {
    sales
  } = options;
  try {
    const successes = [];
    const errors = [];
    await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      await P.mapLimit(sales, async sale => {
        const companyId = sale.data?.data?.company?.id;
        const locationId = sale.data?.data?.location?.id;
        let nApi;
        try {
          if (locationId && locationId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(sale.id);
          } else if (companyId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('sales').obj(sale.id);
          } else {
            nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(sale.id);
          }
          const res = await nApi.srv('recovr').post('createTripacContract');
          if (res && !res?.error && (!res.status || res.status === 200 || res.status === 201)) {
            successes.push(res);
          } else {
            errors.push(res);
            Toast.error(res?.error);
          }
        } catch (err) {
          console.error(`Error creating Tripac Contract: ${err.message}`);
        }
      });
      // Consolidate all successes into a single toast
      const successesLen = successes.length;
      const errorsLen = errors.length;
      const total = successes.length + errors.length;
      const totalPlural = total > 1 ? 'sales' : 'sale';
      if (successesLen === 1 && errorsLen === 0) {
        Toast.success('Successfully created Tripac Contract.');
      } else if (successesLen > 0) {
        Toast.success(`Created Tripac Contracts for ${successesLen} out 
          of ${total} ${totalPlural}.`);
      }
    });
  } catch (err) {
    console.error(`Did not create Tripac Contract: ${err.message}`);
  }
}
export default {
  sell,
  voidSale,
  canForceVoidSale,
  replaceLocator,
  canReplaceLocator,
  changeVehicleOwnership,
  canChangeVehicleOwnership,
  getCustomerInfo,
  sendPendingSaleMessage,
  canSendPendingSaleMessage,
  reconcileSales,
  canReconcileSales,
  markAsLost,
  canMarkAsLost,
  canSellVehicleWithUnpairedLocator,
  addAdditionalTagsToSale,
  canAddAdditionalTagsToSale,
  canCreateTripacContract,
  createTripacContract
};